import EventEmitter from "events";
import _ from "lodash";

class Playable extends EventEmitter {
  constructor({ buffer, context, mixer }) {
    super();
    this.buffer = buffer;
    this.context = context;
    this.mixer = mixer;
    if (!this.buffer) {
      throw new Error("opts.buffer is required.");
    }
    if (!this.context) {
      throw new Error("opts.context is required.");
    }

    this.analysers = [];
    this.source = null;
    this.startTime = 0;
    this.stopTime = 0;
  }

  play(param = {}) {
    return new Promise((resolve, reject) => {
      const { context } = this;
      if (!context) {
        reject(new Error("context is not found."));
        return;
      }

      if (!param.multi) {
        this.stop();
      }

      const source = context.createBufferSource();
      source.buffer = this.buffer;
      source.loop = param.loop || false;

      const volume = _.isNumber(param.volume) ? param.volume : 1;
      const gainNode = context.createGain();
      gainNode.gain.value = volume;
      source.connect(gainNode);
      this.gainNode = gainNode;
      this.sendToOutput(gainNode);

      const when = param.when || 0;
      const offset =
        param.offset && param.offset < this.buffer.duration ? param.offset : 0;
      const duration = param.duration || undefined;
      source.start(when, offset, duration);
      this.startTime = context.currentTime - offset;
      this.source = source;
      resolve();
      this.emit("play");
    });
  }

  resume(param = {}) {
    return this.play({
      ...param,
      offset: this.stopTime || 0
    });
  }

  sendToOutput(node) {
    const { mixer } = this;
    if (mixer) {
      node.connect(mixer);
    }
    this.analysers.forEach(analyser => {
      node.connect(analyser);
    });
  }

  stop() {
    if (!this.source) {
      // 何も流さないという目的は果たしているのでにぎりつぶす
      return Promise.resolve();
    }
    this.stopTime = this.getCurrentTime();
    this.source.stop();
    this.source = null;
    this.emit("stop");
    return Promise.resolve();
  }

  makeWaveGetter({ type = "time-domain", fftSize = 2048 }) {
    const { context } = this;
    const analyser = context.createAnalyser();
    analyser.fftSize = fftSize;
    this.analysers.push(analyser);
    return () => {
      const bufferLength = analyser.frequencyBinCount;
      const dataArray = new Uint8Array(bufferLength);
      if (type === "time-domain") {
        analyser.getByteTimeDomainData(dataArray);
      } else if (type === "frequency") {
        analyser.getByteFrequencyData(dataArray);
      }
      return dataArray;
    };
  }

  getCurrentTime() {
    if (!this.source || !_.isNumber(this.startTime)) return 0;
    return this.context.currentTime - this.startTime;
  }

  getProgress() {
    return [this.getCurrentTime(), this.buffer.duration];
  }

  setVolume(v) {
    if (!this.gainNode) return;
    this.gainNode.gain.value = v;
  }
}

export default class SimpleAudioAPI extends EventEmitter {
  constructor() {
    super();

    this.cache = {};

    this.initAudioContext();
    this.initListeners();
  }

  initAudioContext() {
    let SupportedAudioContext;
    try {
      SupportedAudioContext = window.AudioContext || window.webkitAudioContext;
    } catch (e) {
      /* do nothing */
    }

    if (SupportedAudioContext) {
      const context = new SupportedAudioContext();
      this.context = context;

      const mixer = context.createGain();
      mixer.connect(context.destination);
      this.mixer = mixer;
    }
  }

  initListeners() {
    document.addEventListener("visibilitychange", () => {
      this.applyVisibility();
    });
  }

  applyVisibility() {
    if (document.visibilityState !== "visible") {
      this.mixer.gain.value = 0;
    } else {
      this.mixer.gain.value = 1;
    }
  }

  loadSound(src, opts = {}) {
    return new Promise((resolve, reject) => {
      if (!this.context) {
        reject(new Error("audio context is not found."));
        return;
      }

      if (!src) {
        reject(new Error("src is required."));
        return;
      }

      if (this.cache[src]) {
        resolve(this.cache[src]);
        return;
      }

      const request = new XMLHttpRequest();
      request.open("GET", src, true);
      request.responseType = "arraybuffer";
      request.onload = () => {
        this.context.decodeAudioData(request.response, buffer => {
          const playable = new Playable({
            buffer,
            context: this.context,
            mixer: opts.skipMix ? null : this.mixer
          });
          this.cache[src] = playable;
          resolve(playable);
        });
      };
      request.send();
    });
  }

  ping() {
    return new Promise((resolve, reject) => {
      const { context } = this;
      if (!context) {
        reject(new Error("context is not found."));
        return;
      }

      const source = context.createBufferSource();
      source.start(0, 0, 0);
    });
  }
}
